/**
 * Wie ein Event-Bild auf der Detailseite dargestellt wird.
 *
 * Grund: Der Hero schneidet auf ein festes Format zu (Desktop 1100x300, Handy 360x188). Für ein
 * Foto ist das richtig — für ein Vereinslogo nicht, davon bliebe nach dem Zuschnitt nur ein
 * Ausschnitt des Schriftzugs übrig (siehe Trello „Logo der Veranstaltung").
 *
 * Das Seitenverhältnis allein reicht als Unterscheidung nicht: Das Rhön300-Logo ist 660x400
 * (1,65) und damit genauso „quer" wie ein Foto. Deshalb kommt ein zweites Signal dazu — steht
 * das Motiv auf einer einfarbigen oder durchsichtigen Fläche, ist es eine Grafik, kein Foto.
 */

export type HeroShape =
  /** Foto: Hero oben, auf feste Höhe zugeschnitten (mit Fokuspunkt). */
  | 'photo'
  /** Breite Grafik/Schriftzug: Hero oben, vollständig und unbeschnitten. */
  | 'banner'
  /** Logo im Quadrat- oder Hochformat: eigene Kachel neben den Eckdaten. */
  | 'logo';

/** Ab hier ist eine Grafik so breit, dass sie über die volle Breite besser wirkt als als Kachel. */
const BANNER_RATIO = 2.6;

/** Kantenlänge des verkleinerten Abzugs für die Hintergrund-Probe. */
const SAMPLE_SIZE = 32;

/** Reine Entscheidung — getrennt von der Bildmessung, damit sie testbar bleibt. */
export const heroShapeFrom = (ratio: number, graphic: boolean): HeroShape => {
  if (!graphic) return 'photo';
  return ratio >= BANNER_RATIO ? 'banner' : 'logo';
};

/**
 * Steht das Motiv auf einer einfarbigen oder durchsichtigen Fläche?
 *
 * Geprüft werden die vier Ecken eines auf [SAMPLE_SIZE] verkleinerten Abzugs; durch das
 * Verkleinern ist jeder Pixel bereits der Mittelwert eines ganzen Eckbereichs.
 *
 * Bewusst streng: Nur durchsichtige oder helle, untereinander praktisch gleiche Ecken zählen als
 * Grafik. Ein Foto mit gleichmäßigem Himmel hat unten andere Ecken als oben; ein dunkles Foto
 * mit Vignette wird nicht als Logo missdeutet, weil Helligkeit verlangt wird. Im Zweifel „Foto" —
 * das ist die bisherige Darstellung.
 */
export const cornersLookLikeFlatBackground = (
  pixels: Uint8ClampedArray,
  size: number = SAMPLE_SIZE,
): boolean => {
  const inset = 1;
  const at = (x: number, y: number): [number, number, number, number] => {
    const i = (y * size + x) * 4;
    return [pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]];
  };
  const max = size - 1 - inset;
  const corners = [at(inset, inset), at(max, inset), at(inset, max), at(max, max)];

  // Durchsichtige Ecken gibt es bei einem Foto nicht.
  if (corners.every(c => c[3] < 16)) return true;
  if (corners.some(c => c[3] < 250)) return true;

  const spread = (channel: number): number => {
    const values = corners.map(c => c[channel]);
    return Math.max(...values) - Math.min(...values);
  };
  const sameColour = spread(0) <= 12 && spread(1) <= 12 && spread(2) <= 12;
  const light = corners.every(c => (c[0] + c[1] + c[2]) / 3 >= 225);
  return sameColour && light;
};

/** Bild laden und sein Seitenverhältnis melden. */
const loadRatio = (url: string): Promise<number> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth / Math.max(1, img.naturalHeight));
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.src = url;
  });

/**
 * Hintergrund-Probe. Braucht Pixel-Zugriff, deshalb ein zweiter Ladevorgang mit
 * `crossOrigin` — ohne den wäre die Canvas bei fremden Bild-URLs gesperrt. Schlägt das fehl
 * (fremder Server ohne CORS-Freigabe), gibt es kein Ergebnis statt eines geratenen.
 */
const probeFlatBackground = (url: string): Promise<boolean | null> =>
  new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        resolve(cornersLookLikeFlatBackground(ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data));
      } catch {
        // Canvas gesperrt (fremdes Bild ohne CORS-Header) — kein Pixel-Zugriff.
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Misst das Bild und entscheidet über die Darstellung.
 *
 * Ohne Pixel-Zugriff bleibt das Seitenverhältnis: alles ab [BANNER_RATIO] gilt dann als Grafik —
 * so breit ist praktisch nur ein Schriftzug, und ein Zuschnitt würde ihn zerstören.
 *
 * @throws wenn das Bild gar nicht lädt (tote URL) — die Detailseite blendet es dann aus.
 */
export const analyzeHeroImage = async (url: string): Promise<HeroShape> => {
  const ratio = await loadRatio(url);
  const flat = await probeFlatBackground(url);
  return heroShapeFrom(ratio, flat ?? ratio >= BANNER_RATIO);
};
